import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import classNames from 'classnames'
import { formatAtRiskAppointmentStart } from 'src/modules/alerts/paused-reminders-modal/paused-reminders-modal.domain'
import { FormattedText } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import { ReactComponent as BellCalendarIcon } from '../icons/icon-bell-calendar.svg'
import { ReactComponent as BellOffIcon } from '../icons/icon-bell-off.svg'
import { ReactComponent as CalendarIcon } from '../icons/icon-calendar.svg'
import type { MissingRemindersPanelProps, MissingRemindersTileProps } from './missing-reminders-panel.interface'
import classes from './missing-reminders-panel.module.scss'

const NOT_LOADED_VALUE = '-'

export function MissingRemindersPanel({ reason, pausedReminders }: MissingRemindersPanelProps) {
  const { timezone } = useDisplayTimezone()
  const { isReady, notSentCount, atRisk } = pausedReminders

  const nextAtRisk = atRisk.next ? (
    formatAtRiskAppointmentStart(atRisk.next, timezone)
  ) : (
    <FormattedText id="dashboard.paused.panel.nextAtRiskNone" />
  )

  return (
    <section className={classes.panel} data-testid="missing-reminders-panel">
      <div className={classes.header}>
        <h3 className={classes.title}>
          <FormattedText id="dashboard.paused.panel.title" />
        </h3>
        <p className={classes.subtitle}>
          <FormattedText
            id={
              reason === REMINDERS_PAUSED_REASONS.paymentPaused
                ? 'dashboard.paused.panel.subtitlePaymentPaused'
                : 'dashboard.paused.panel.subtitle'
            }
          />
        </p>
      </div>
      <div className={classes.tiles}>
        <MissingRemindersTile
          icon={<BellOffIcon />}
          labelId="dashboard.paused.panel.notSent"
          value={isReady ? notSentCount : NOT_LOADED_VALUE}
          variant="highlighted"
          testId="missing-reminders-not-sent"
        />
        <MissingRemindersTile
          icon={<BellCalendarIcon />}
          labelId="dashboard.paused.panel.upcoming"
          value={isReady ? atRisk.count : NOT_LOADED_VALUE}
          variant="count"
          testId="missing-reminders-upcoming"
        />
        <MissingRemindersTile
          icon={<CalendarIcon />}
          labelId="dashboard.paused.panel.nextAtRisk"
          value={isReady ? nextAtRisk : NOT_LOADED_VALUE}
          variant="date"
          testId="missing-reminders-next-at-risk"
        />
      </div>
    </section>
  )
}

function MissingRemindersTile({ icon, labelId, value, variant, testId }: MissingRemindersTileProps) {
  return (
    <div
      className={classNames(classes.tile, { [classes.tileHighlighted]: variant === 'highlighted' })}
      data-testid={testId}
    >
      <span className={classes.icon}>{icon}</span>
      <div className={classNames(classes.tileContent, { [classes.tileContentStacked]: variant === 'date' })}>
        <span className={classNames(classes.label, { [classes.labelHighlighted]: variant === 'highlighted' })}>
          <FormattedText id={labelId} />
        </span>
        <span
          className={classNames(classes.value, {
            [classes.valueHighlighted]: variant === 'highlighted',
            [classes.valueDate]: variant === 'date',
          })}
        >
          {value}
        </span>
      </div>
    </div>
  )
}
